
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { SiteSettings } from "@/lib/types";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Lock } from "lucide-react";

const SettingsManager = () => {
  const [settings, setSettings] = useState<SiteSettings>({
    registrationEnabled: true,
  });
  const [isLoading, setIsLoading] = useState(true);
  const { user, isAdmin } = useAuth();

  // Fetch settings when component mounts
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .maybeSingle();

      if (error) {
        console.error('Error fetching settings:', error);
        toast.error('Failed to load settings');
      }

      if (data) {
        setSettings({
          registrationEnabled: data.registration_enabled,
        });
      } else {
        // If no settings found and user is admin, create default settings
        if (isAdmin) {
          const { error: insertError } = await supabase
            .from('site_settings')
            .insert({ 
              id: 1, 
              registration_enabled: true
            });
            
          if (insertError) {
            console.error('Error creating default settings:', insertError);
            toast.error('Failed to create default settings');
          } else {
            setSettings({ registrationEnabled: true });
          }
        }
      }
    } catch (err) {
      console.error('Error processing settings:', err);
      toast.error('Error loading settings');
    } finally {
      setIsLoading(false);
    }
  };

  const updateRegistrationEnabled = async (enabled: boolean) => {
    // Security check - only allow admins to update settings
    if (!isAdmin) {
      toast.error("You don't have permission to change settings");
      return;
    }
    
    try {
      // Update local state immediately for responsive UI
      setSettings(prev => ({ ...prev, registrationEnabled: enabled }));
      
      const { error } = await supabase
        .from('site_settings')
        .upsert({ 
          id: 1, // We use a single row with ID 1 for site settings
          registration_enabled: enabled 
        });

      if (error) {
        console.error('Error updating setting:', error);
        toast.error('Failed to update setting');
        // Revert local state if server update fails
        setSettings(prev => ({ ...prev, registrationEnabled: !enabled }));
        return;
      }

      toast.success(`User registration ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error('Error saving settings');
      // Revert local state if any error occurs
      setSettings(prev => ({ ...prev, registrationEnabled: !enabled }));
    }
  };

  // Show the loading state instead of nothing
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Site Settings</CardTitle>
          <CardDescription>
            Loading settings...
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!user) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Site Settings</CardTitle>
          <CardDescription>
            Authentication required
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Lock size={16} />
            <span>You must be logged in to access site settings</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Site Settings
          {!isAdmin && (
            <Badge variant="destructive" className="ml-2">
              <AlertTriangle className="h-3 w-3 mr-1" /> Limited Access
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Configure global settings for your application
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between space-x-2">
          <div>
            <Label htmlFor="registration-toggle" className="font-medium">User Registration</Label>
            <p className="text-sm text-muted-foreground">
              Allow new users to register on the site
            </p>
          </div>
          <Switch 
            id="registration-toggle"
            checked={settings.registrationEnabled}
            onCheckedChange={updateRegistrationEnabled}
            disabled={isLoading || !isAdmin}
          />
        </div>
        
        {!isAdmin && (
          <div className="mt-4 p-3 bg-amber-50 text-amber-800 rounded-md text-sm border border-amber-200">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              <strong>Admin privileges required</strong>
            </div>
            <p className="mt-1">
              Only administrators can modify site settings.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SettingsManager;
